import React, { useContext, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Items/Navbar';
import AuthContext from '../shared/AuthContext';
import FormattedQuestions from '../Items/FormattedQuestions';
import LearningGuide from '../learning/LearningGuide';
import StudyHubBackNav from '../navigation/StudyHubBackNav';
import NavShortcutGrid, { NavShortcut } from '../navigation/NavShortcutGrid';
import LearningTags from '../learning/LearningTags';
import '../navigation/appNav.css';
import '../learning/learning-page-ui.css';
import '../learning/LearningFlashcard.css';
import './styles/SavedPage.css';
import { invalidateRealtime, REALTIME_INVALIDATE_EVENT } from '../../realtime/realtimeBus';

const SavedPage = () => {
  const { apiClient, user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const userProfile = user || JSON.parse(localStorage.getItem('userProfile') || 'null');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/learning/saved');
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onInvalidate = () => load();
    window.addEventListener(REALTIME_INVALIDATE_EVENT, onInvalidate);
    return () => window.removeEventListener(REALTIME_INVALIDATE_EVENT, onInvalidate);
  }, [load]);

  const unsave = async (questionId) => {
    try {
      await apiClient.delete(`/api/learning/saved/${questionId}`);
      setItems((prev) => prev.filter((q) => q.id !== questionId));
      invalidateRealtime('save-toggle');
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="learning-page saved-page">
      <Navbar userProfile={userProfile} loading={loading} />
      <main className="learning-page-shell">
        <StudyHubBackNav />
        <h1 className="learning-page-title">Saved</h1>
        <LearningGuide focus="saved" />
        <p className="learning-page-lead">Questions you bookmarked for practice and review.</p>
        <NavShortcutGrid>
          <NavShortcut to="/practice" label="Practice deck" description="Study saved cards" variant="primary" />
          <NavShortcut to="/review" label="Review" description="When items are due" />
          <NavShortcut to="/library" label="Library" description="Find more to save" />
        </NavShortcutGrid>
        {error && <p role="alert" className="learning-alert">{error}</p>}
        {!loading && items.length === 0 && (
          <div className="learning-empty">
            <p>No saved questions yet. Open the library and tap Save for practice on any card.</p>
            <NavShortcutGrid>
              <NavShortcut to="/library" label="Go to Library" variant="primary" description="Browse notebooks" />
            </NavShortcutGrid>
          </div>
        )}
        <ul className="learning-list">
          {items.map((item) => (
            <li key={item.id}>
              <article className="learning-list-card">
                <LearningTags item={item} />
                <div className="learning-list-card-body">
                  <Link to={`/question/${item.id}`} className="learning-list-card-question">
                    <FormattedQuestions item={item} />
                  </Link>
                </div>
                <div className="learning-list-card-actions">
                  <Link to={`/question/${item.id}`} className="qd-btn qd-btn--primary">
                    Open
                  </Link>
                  <Link to="/practice" className="qd-btn qd-btn--secondary">
                    Practice
                  </Link>
                  <button type="button" className="qd-btn qd-btn--ghost" onClick={() => unsave(item.id)}>
                    Remove
                  </button>
                </div>
              </article>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
};

export default SavedPage;
